<template>
  <!-- fullPage renders the same form inline (SignUpView) instead of as an overlay. The page container
       must not use .modalContainer: App.vue locks body scroll whenever one is in the DOM. -->
  <div
    @click.self="!fullPage && closeModal()"
    :class="fullPage ? 'pageContainer' : 'modalContainer'"
    data-cy="create-account-modal"
  >
    <div :class="fullPage ? 'pageWrapper' : 'modalWrapper'">
      <div
        v-if="!fullPage"
        @click="closeModal"
        @keydown.enter.prevent="closeModal"
        @keydown.space.prevent="closeModal"
        class="close"
        role="button"
        tabindex="0"
        :aria-label="t('common.closeModal')"
        data-cy="close-create-account-modal"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.1161 6.6228L7.75908 6.97635L8.11437 7.33163L13.4572 12.6745L12.6745 13.4572L11.8466 12.6294L11.8449 12.6277L7.37718 8.11608L7.02364 7.75906L6.66835 8.11435L1.32551 13.4572L0.542786 12.6745L5.88563 7.33163L6.24062 6.97664L5.8842 6.62309L0.543158 1.32514L1.32551 0.542787L6.66835 5.88563L7.02334 6.24061L7.37689 5.88419L12.6748 0.543155L13.4572 1.32551L12.6294 2.15333L12.6277 2.15504L8.1161 6.6228ZM12.7648 13.5475C12.7649 13.5476 12.765 13.5478 12.7651 13.5479L12.7648 13.5475ZM0.452113 12.7651L0.452145 12.7651C0.452134 12.7651 0.452123 12.7651 0.452113 12.7651Z"
            fill="black"
            stroke="black"
          />
        </svg>
      </div>
      <img class="logo" :src="getMenuLogo" :alt="t('common.companyLogoAlt')" />
      <h2 style="text-align: center">{{ t('signUp.title') }}</h2>
      <div class="login">
        <p>{{ t('signUp.alreadyHaveAccount') }}</p>
        <a @click="loginClicked" href="javascript:;"> {{ t('login.login') }} </a>
      </div>
      <div class="nameWrapper">
        <div class="inputWrapper">
          <label for="firstName">{{ t('profile.fields.firstName') }}</label>
          <input
            @keyup.enter="signUp"
            v-model="signUpInfo.firstName"
            @input="formatNameWhileTyping(signUpInfo.firstName)"
            type="text"
            :placeholder="t('profile.fields.firstName')"
            id="firstName"
            data-cy="first-name-input"
          />
          <p v-if="v$.signUpInfo.firstName.$error === true" class="validation" data-cy="first-name-validation-error">
            {{ t('profile.validation.firstName') }}
          </p>
        </div>
        <div class="inputWrapper">
          <label for="lastName">{{ t('profile.fields.lastName') }}</label>
          <input
            @keyup.enter="signUp"
            v-model="signUpInfo.lastName"
            @input="formatLastNameWhileTyping(signUpInfo.lastName)"
            type="text"
            :placeholder="t('profile.fields.lastName')"
            id="lastName"
            data-cy="last-name-input"
          />
          <p v-if="v$.signUpInfo.lastName.$error === true" class="validation" data-cy="last-name-validation-error">
            {{ t('profile.validation.lastName') }}
          </p>
        </div>
      </div>
      <div class="inputWrapper">
        <label for="email">{{ t('profile.fields.email') }}</label>
        <input
          @keyup.enter="signUp"
          v-model="signUpInfo.email"
          @input="formatEmailWhileTyping(signUpInfo.email)"
          type="email"
          :placeholder="t('profile.fields.email')"
          id="email"
          inputmode="email"
          data-cy="create-account-email-input"
        />
        <p
          v-if="v$.signUpInfo.email.$error || !isValidEmail(signUpInfo.email)"
          class="validation"
          data-cy="email-validation-error"
        >
          {{ t('login.validation.enterEmail') }}
        </p>
      </div>
      <div class="inputWrapper">
        <label for="companyName">{{ t('signUp.companyName') }}</label>
        <input
          @keyup.enter="signUp"
          v-model="signUpInfo.companyName"
          type="text"
          :placeholder="t('signUp.companyNamePlaceholder')"
          id="companyName"
        />
      </div>
      <div class="inputWrapper">
        <label for="birthday">{{ t('profile.fields.birthday') }}</label>
        <input
          @keyup.enter="signUp"
          @input="formatBirthdateWhileTyping"
          v-model="signUpInfo.birthdate"
          type="text"
          :placeholder="t('profile.fields.birthdayPlaceholder')"
          id="birthday"
          inputmode="numeric"
          data-cy="birthday-input"
        />
        <p class="info">{{ t('signUp.birthdayInfo') }}</p>
        <p v-if="birthdateError" class="validation">{{ birthdateError }}</p>
      </div>
      <div class="inputWrapper">
        <template v-if="enableInternationalPhoneInput">
          <IntlTelInput
            ref="intlPhoneInput"
            v-model="signUpInfo.phone"
            id="phone"
            :label="t('phoneVerification.cellPhone')"
            :placeholder="t('phoneVerification.cellPhone')"
            :use-validation="true"
            :required="true"
            :initial-country="initialPhoneCountry"
            @enter="signUp"
            @validityChange="handleIntlPhoneValidity"
            @countryChange="handleCountryChange"
          />
        </template>
        <template v-else>
          <label for="phone">{{ t('phoneVerification.cellPhone') }}</label>
          <input
            @keyup.enter="signUp"
            @input="formatPhoneNumberWhileTyping(signUpInfo.phone)"
            v-model="signUpInfo.phone"
            type="text"
            :placeholder="t('phoneVerification.cellPhone')"
            id="phone"
            inputmode="tel"
            data-cy="phone-input"
          />
          <p v-if="v$.signUpInfo.phone.$error === true" class="validation" data-cy="phone-validation-error">
            {{ t('profile.validation.phone') }}
          </p>
          <p v-if="validPhoneNumberError" class="validation" data-cy="phone-validation-error">
            {{ validPhoneNumberErrorMessage }}
          </p>
        </template>
        <p class="info">{{ t('missingInfo.smsInfo') }}</p>
      </div>
      <div class="inputWrapper">
        <label for="password">{{ t('login.password') }}</label>
        <input
          @keyup.enter="signUp"
          v-model="signUpInfo.password"
          :type="passwordInputType"
          :placeholder="t('login.password')"
          id="password"
          class="rr-mask"
          data-cy="create-account-password-input"
        />
        <p
          v-if="
            v$.signUpInfo.password.$errors.length != 0 && v$.signUpInfo.password.$errors[0].$validator == 'required'
          "
          class="validation"
          data-cy="password-validation-error"
        >
          {{ t('login.validation.enterPassword') }}
        </p>
        <p v-if="passwordIsValid === false" class="validation" data-cy="password-length-validation-error">
          {{ t('login.validation.validPassword') }}
        </p>
        <a @click="togglePassword" class="passwordToggle" href="#">
          <img :src="passwordIconPath" :alt="t('common.passwordIconAlt')" />
        </a>
        <PasswordValidator :password="signUpInfo.password" @valid="checkIfPasswordValid" />
      </div>
      <div v-if="storeConfig?.email_optin || storeConfig?.sms_optin" class="optIn">
        <div v-if="storeConfig?.email_optin" class="checkboxWrapper">
          <input type="checkbox" id="emainOptin" v-model="signUpInfo.emailOptin" />
          <label class="checkboxLabel" for="emainOptin">{{ t('profile.optin.email') }}</label>
        </div>
        <div v-if="storeConfig?.sms_optin" class="checkboxWrapper">
          <input type="checkbox" id="smsOptin" v-model="signUpInfo.phoneOptin" />
          <label class="checkboxLabel" for="smsOptin">{{ t('profile.optin.sms') }}</label>
        </div>
      </div>
      <div class="consent">
        <p>
          {{ t('signUp.consentText') }} <br />
          {{ brandNameDisplay }}
          <span><a :href="termsOfServiceUrl" target="blank_">{{ t('signUp.termsOfService') }}</a></span>
          &
          <span><a :href="privacyPolicy" target="blank_">{{ t('signUp.privacyPolicy') }}</a></span>
        </p>
      </div>
      <p class="feedback error" v-if="error.status">{{ error.message }}</p>
      <button @click="signUp" :disabled="buttonLoader" data-cy="create-account-button">
        <div v-if="buttonLoader" class="loader buttonLoader"></div>
        <template v-if="!buttonLoader">{{ t('signUp.signUp') }}</template>
      </button>
      <div v-if="branding?.sso?.google?.enabled || branding?.sso?.apple?.enabled" class="seperator">
        <div class="line"></div>
        <p>{{ t('signUp.or') }}</p>
        <div class="line"></div>
      </div>
      <div
        v-if="branding?.sso?.google?.enabled || (branding?.sso?.apple?.enabled && checkAppleSSOAvailable)"
        class="ssoButtons"
      >
        <GoogleSSO
          v-if="branding?.sso?.google?.enabled"
          @getCustomerInfo="getCustomerInfo"
          @ssoError="ssoError"
          @showLoader="showLoader"
          id="googleButton"
        />
        <AppleSSO
          v-if="branding?.sso?.apple?.enabled && checkAppleSSOAvailable"
          @getCustomerInfo="getCustomerInfo"
          @ssoError="ssoError"
          @showLoader="showLoader"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import useVuelidate from '@vuelidate/core'
import { required, email, minLength } from '@vuelidate/validators'

import { mapState, mapMutations } from 'vuex'
import { useI18n } from 'vue-i18n'

import modalMixins from '@/mixins/modalMixins.js'
import formattingMixins from '@/mixins/formattingMixins'
import coralogixMixins from '@/mixins/coralogixMixins'

import customersApi from '@/api/customersApi.js'

import PasswordValidator from '@/components/Elements/PasswordValidator.vue'
import GoogleSSO from '@/components/Elements/GoogleSSO.vue'
import AppleSSO from '@/components/Elements/AppleSSO.vue'
import IntlTelInput from '@/components/Elements/IntlTelInput.vue'
import { handleSiftLogin } from '@/utils/siftAuth'
import { useFlag } from '@unleash/proxy-client-vue'

export default {
  setup() {
    const { t, locale } = useI18n()
    const enableInternationalPhoneInput = useFlag('enable_international_phone_input', { fallback: false })
    return {
      t,
      locale,
      v$: useVuelidate(),
      enableInternationalPhoneInput,
    }
  },
  data() {
    return {
      signUpInfo: {
        firstName: this.firstName || '',
        lastName: this.lastName || '',
        email: this.email || '',
        phone: this.formatPhoneNumber(this.phone) || '',
        password: '',
        birthdate: '',
        companyName: '',
        // pre-selected by default (LBX-28538); only sent when the tenant has email_optin on
        emailOptin: true,
        phoneOptin: false,
      },
      passwordInputType: 'password',
      passwordIconPath: require('@/assets/icons/passwordShow.svg'),
      error: {
        status: false,
        message: '',
      },
      passwordIsValid: null,
      validPhoneNumberError: false,
      validPhoneNumberErrorMessage: '',
      birthdateError: null,
      brandName: 'Lunchbox',
      termsOfServiceUrl: 'https://lunchbox.io/terms',
      privacyPolicy: 'https://lunchbox.io/privacy',
      phoneCountryCode: null,
    }
  },
  validations() {
    return {
      signUpInfo: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        phone: this.enableInternationalPhoneInput ? {} : { required },
        password: {
          required,
          minLength: minLength(6),
        },
        birthdate: this.signUpInfo.birthdate ? { required, birthdateValid: this.validateBirthdate } : {},
      },
    }
  },
  components: {
    PasswordValidator,
    GoogleSSO,
    AppleSSO,
    IntlTelInput,
  },
  props: {
    email: null,
    phone: null,
    firstName: null,
    lastName: null,
    countryCode: null,
    fullPage: { type: Boolean, default: false },
  },
  mixins: [modalMixins, formattingMixins, coralogixMixins],
  computed: {
    ...mapState(['buttonLoader', 'branding', 'cookies', 'storeConfig', 'copperDetails']),
    // The consent line reads "<brand>'s Terms of Service" in English. The possessive is
    // English grammar, so other locales take the bare brand name.
    brandNameDisplay() {
      if (!this.locale?.startsWith('en')) return this.brandName
      return this.brandName.endsWith("'s") ? this.brandName : `${this.brandName}'s`
    },
    getMenuLogo() {
      return this.branding.menu_logo_url
    },
    checkAppleSSOAvailable() {
      if (!window.AppleID) return false
      return true
    },
    initialPhoneCountry() {
      // If international phone input is disabled, the value is ignored but keep a sensible default
      if (!this.enableInternationalPhoneInput) {
        return 'us'
      }
      if (this.countryCode) {
        return this.countryCode.toLowerCase()
      }
      if (this.storeConfig?.country_code) {
        return this.storeConfig.country_code.toLowerCase()
      }
      return 'us'
    },
  },
  watch: {
    'signUpInfo.phone'(newValue) {
      // Clear validation errors when phone is empty or only contains formatting characters
      if (!newValue || newValue.replace(/[^\d]/g, '').length === 0) {
        this.validPhoneNumberError = false
        if (this.v$ && this.v$.signUpInfo && this.v$.signUpInfo.phone) {
          this.v$.signUpInfo.phone.$reset()
        }
      }
    },
  },
  emits: ['closeModal', 'toggleCopperInfo', 'loginClicked', 'accountCreated', 'openVerifyEmailModal'],
  methods: {
    ...mapMutations([
      'SET_LOGGED_IN',
      'SET_BUTTON_LOADER',
      'SET_CUSTOMER_DATA',
      'SET_PHONE_VERIFIED',
      'SET_COOKIES',
      'SET_TOASTER',
    ]),
    closeModal() {
      this.$emit('closeModal')
    },
    toggleCopperInfo() {
      this.$emit('toggleCopperInfo')
    },
    loginClicked() {
      // On the full page, closeModal means "signup finished" and triggers the post-signup redirect
      if (!this.fullPage) this.$emit('closeModal')
      this.$emit('loginClicked')
    },
    checkIfPasswordValid(valid) {
      this.passwordIsValid = valid
    },
    ssoError() {
      this.error.status = true
      this.error.message = this.t('common.somethingWentWrong')
      this.SET_BUTTON_LOADER(false)
    },
    showLoader() {
      this.SET_BUTTON_LOADER(true)
    },
    handleIntlPhoneValidity({ countryData }) {
      if (countryData && countryData.iso2) {
        this.phoneCountryCode = countryData.iso2.toUpperCase()
      }
    },
    handleCountryChange(countryData) {
      if (countryData && countryData.iso2) {
        this.phoneCountryCode = countryData.iso2.toUpperCase()
      } else {
        this.phoneCountryCode = null
      }
    },
    async signUp() {
      this.v$.$touch()

      if (this.enableInternationalPhoneInput) {
        const intlInputComponent = this.$refs.intlPhoneInput
        if (intlInputComponent && typeof intlInputComponent.setTouched === 'function') {
          intlInputComponent.setTouched(true)
        }
      }

      if (!this.isValidEmail(this.signUpInfo.email)) {
        this.error.status = true
        this.error.message = this.t('login.validation.validEmail')
        return
      }
      if (this.v$.$error || this.passwordIsValid === null || this.passwordIsValid === false) {
        return
      }

      if (this.enableInternationalPhoneInput) {
        const intlInputComponent = this.$refs.intlPhoneInput
        if (!intlInputComponent || !intlInputComponent.validate(true, true)) {
          return
        }
        const countryData = intlInputComponent.getSelectedCountryData()
        if (countryData && countryData.iso2) {
          this.phoneCountryCode = countryData.iso2.toUpperCase()
        }
        this.validPhoneNumberError = false
      } else {
        // Validate with Vuelidate first
        if (this.v$.signUpInfo.phone.$error) {
          return
        }

        // Check if phone is empty
        if (!this.signUpInfo.phone || this.signUpInfo.phone.trim() === '') {
          this.v$.signUpInfo.phone.$touch()
          return
        }

        // Check phone validation before sending code
        if (!this.isValidPhoneNumber(this.signUpInfo.phone)) {
          this.validPhoneNumberError = true
          this.validPhoneNumberErrorMessage = this.t('profile.validation.phoneInvalid')
          return
        }
        this.validPhoneNumberError = false
        this.validPhoneNumberErrorMessage = ''

        // Check phone length for non-international input
        if (this.signUpInfo.phone.length > 0 && this.signUpInfo.phone.length < 14) {
          this.validPhoneNumberError = true
          this.validPhoneNumberErrorMessage = this.t('profile.validation.phoneShort')
          return
        }
        this.phoneCountryCode = null
      }

      // Clear any previous errors before proceeding
      this.error.status = false
      this.error.message = ''
      this.SET_BUTTON_LOADER(true)

      let formattedBirthdate = ''
      if (this.signUpInfo.birthdate != '') {
        // check if birthdate matches MM/DD/YYYY
        if (this.signUpInfo.birthdate.length > 0 && !moment(this.signUpInfo.birthdate, 'MM/DD/YYYY', true).isValid()) {
          this.error.status = true
          this.error.message = this.t('profile.validation.birthdayFormat')
          this.SET_BUTTON_LOADER(false)
          return
        }

        // get todays date
        let todaysDate = moment().format('YYYY-MM-DD')
        // check if birthdate is after todaysDate
        if (
          this.signUpInfo.birthdate.length > 0 &&
          moment(this.signUpInfo.birthdate, 'MM/DD/YYYY').isAfter(todaysDate, 'YYYY-MM-DD')
        ) {
          this.error.status = true
          this.error.message = this.t('profile.validation.birthdayFuture')
          this.SET_BUTTON_LOADER(false)
          return
        }

        // get the date from 13 years ago
        let maxDate = moment().subtract(13, 'years').format('YYYY-MM-DD')
        // check if birthdate is after maxDate
        if (
          this.signUpInfo.birthdate.length > 0 &&
          moment(this.signUpInfo.birthdate, 'MM/DD/YYYY').isAfter(maxDate, 'YYYY-MM-DD')
        ) {
          this.error.status = true
          this.error.message = this.t('signUp.validation.age')
          this.SET_BUTTON_LOADER(false)
          return
        }

        if (this.signUpInfo.birthdate.length > 0) {
          // change birthdate format from mm/dd/yyyy to yyyy-mm-dd
          let splittedBirthdate = this.signUpInfo.birthdate.split('/')
          formattedBirthdate = `${splittedBirthdate[2]}-${splittedBirthdate[0]}-${splittedBirthdate[1]}`
        }
      }

      // Prepare phone number for payload
      let phoneNumber = ''

      // If using international phone input, get the national number (without country code)
      if (this.enableInternationalPhoneInput) {
        const intlInputComponent = this.$refs.intlPhoneInput
        if (intlInputComponent && typeof intlInputComponent.getNationalNumber === 'function') {
          // Use the component's method to get national number (without country code)
          phoneNumber = intlInputComponent.getNationalNumber()
        } else {
          // Fallback: manually extract national number
          phoneNumber = this.signUpInfo.phone.replace(/\D/g, '')
          const countryData = intlInputComponent?.getSelectedCountryData()
          if (countryData && countryData.dialCode && phoneNumber.startsWith(countryData.dialCode)) {
            phoneNumber = phoneNumber.substring(countryData.dialCode.length)
          }
        }
      } else {
        // For non-international input, just remove non-digits
        phoneNumber = this.signUpInfo.phone.replace(/\D/g, '')
      }

      const payload = {
        email: this.signUpInfo.email,
        password: this.signUpInfo.password,
        first_name: this.signUpInfo.firstName,
        last_name: this.signUpInfo.lastName,
        phone: phoneNumber,
        auto_login: true,
        email_optin: this.storeConfig.email_optin ? this.signUpInfo.emailOptin : false,
        sms_optin: this.storeConfig.sms_optin ? this.signUpInfo.phoneOptin : false,
        birthdate: formattedBirthdate,
        company_name: this.signUpInfo.companyName,
      }

      if (this.enableInternationalPhoneInput && this.phoneCountryCode) {
        payload.country_code = this.phoneCountryCode
      }

      if (!this.signUpInfo.companyName) {
        delete payload.company_name
      }

      if (this.signUpInfo.birthdate == '') {
        delete payload.birthdate
      }

      customersApi
        .createAccount(payload)
        .then((response) => {
          this.SET_CUSTOMER_DATA(response.data.data)
          this.identifyUser(response.data.data)
          this.SET_PHONE_VERIFIED(response.data.data.is_phone_verified)
          localStorage.setItem('phoneVerified', response.data.data.is_phone_verified)
          this.SET_COOKIES(response.data.cookies)
          // ✅ Unified Sift tracking
          try {
            const { customer_id, email } = response.data.data
            if (customer_id) {
              handleSiftLogin(String(customer_id), 'create_account', { email })
            }
          } catch (e) {
            console.warn('Sift signup tracking failed:', e)
          }

          // Facebook Pixel CompleteRegistration event
          if (typeof fbq === 'function') {
            fbq('track', 'CompleteRegistration')
          }
          if (response.data.data.customer_id) {
            this.SET_LOGGED_IN(true)
            localStorage.setItem('customerId', response.data.data.customer_id)
            if (response.data.data.auth_cookie) {
              localStorage.setItem('auth', 'Basic ' + response.data.data.auth_cookie)
            } else {
              // Some tenants do not return auth_cookie on registration (even with auto_login=true),
              // which would otherwise persist a broken "Basic undefined" value and break subsequent requests.
              localStorage.removeItem('auth')
              this.login()
              return
            }
            if (this.storeConfig.require_phone_verification) {
              this.$emit('accountCreated', response.data.data)
            }
            if (this.storeConfig.require_email_verification) {
              this.$emit('openVerifyEmailModal')
            }
            if (this.copperDetails) {
              this.toggleCopperInfo()
            }
            this.closeModal()
            this.SET_BUTTON_LOADER(false)
            this.SET_COOKIES(response.data.cookies)
          } else {
            this.login()
          }
        })
        .catch((error) => {
          this.SET_BUTTON_LOADER(false)
          this.error.status = true
          this.error.message = error?.response?.data?.data?.error || this.t('common.somethingWentWrong')
        })
    },
    login() {
      let payload = {
        username: this.signUpInfo.email,
        password: this.signUpInfo.password,
      }

      customersApi
        .login(payload)
        .then((response) => {
          this.SET_LOGGED_IN(true)
          localStorage.setItem('customerId', response.data.data.customer_id)
          if (response.data.data.auth_cookie) {
            localStorage.setItem('auth', 'Basic ' + response.data.data.auth_cookie)
          } else {
            localStorage.removeItem('auth')
          }

          // ✅ Track Sift login
          const { customer_id, email } = response.data.data
          if (customer_id) {
            handleSiftLogin(String(customer_id), 'login', { email: email || this.signUpInfo.email })
          }

          if (this.storeConfig.require_phone_verification) {
            this.$emit('accountCreated', response.data.data)
          }
          if (this.storeConfig.require_email_verification) {
            this.$emit('openVerifyEmailModal')
          }
          if (this.copperDetails) {
            this.toggleCopperInfo()
          }
          this.closeModal()
          this.SET_BUTTON_LOADER(false)
          this.SET_COOKIES(response.data.cookies)
        })
        .catch(() => {
          this.SET_BUTTON_LOADER(false)
          this.error.status = true
          this.error.message = this.t('common.somethingWentWrong')
        })
    },
    getCustomerInfo() {
      this.SET_BUTTON_LOADER(true)
      customersApi
        .getCustomerInfo()
        .then((response) => {
          let toasterData = {
            show: true,
            message: this.t('login.welcomeBack'),
          }
          this.SET_TOASTER(toasterData)
          this.SET_CUSTOMER_DATA(response.data.data)
          // for FullStory
          this.identifyUser(response.data.data)
          this.SET_PHONE_VERIFIED(response.data.data.is_phone_verified)
          localStorage.setItem('phoneVerified', response.data.data.is_phone_verified)
          this.SET_LOGGED_IN(true)
          if (this.copperDetails) {
            this.toggleCopperInfo()
          }
          this.closeModal()
          this.SET_BUTTON_LOADER(false)
          this.SET_COOKIES(response.data.cookies)
        })
        .catch((error) => {
          console.log(error)
          this.loading = false
          this.error.status = true
          this.error.message = this.t('common.somethingWentWrong')
        })
    },
    formatBirthdateWhileTyping(event) {
      let birthdate = event.target.value

      // Remove any non-numeric and non-slash characters
      birthdate = birthdate.replace(/[^0-9/]/g, '')

      // Handle deletion gracefully
      if (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') {
        this.signUpInfo.birthdate = birthdate
        return
      }

      // Automatically format the birthdate with slashes
      if (birthdate.length > 2 && birthdate[2] !== '/') {
        birthdate = birthdate.slice(0, 2) + '/' + birthdate.slice(2)
      }
      if (birthdate.length > 5 && birthdate[5] !== '/') {
        birthdate = birthdate.slice(0, 5) + '/' + birthdate.slice(5)
      }

      // Limit to 10 characters (mm/dd/yyyy)
      if (birthdate.length > 10) {
        birthdate = birthdate.slice(0, 10)
      }

      // Update the input value
      this.signUpInfo.birthdate = birthdate
    },
    togglePassword() {
      if (this.passwordInputType == 'password') {
        this.passwordInputType = 'text'
        this.passwordIconPath = require('@/assets/icons/passwordHide.svg')
      } else {
        this.passwordInputType = 'password'
        this.passwordIconPath = require('@/assets/icons/passwordShow.svg')
      }
    },
    validateBirthdate(value) {
      const birthdate = new Date(value)
      const year = birthdate.getFullYear()
      if (year < 1900) {
        this.birthdateError = this.t('profile.validation.birthdayTooOld')
        return false
      }
      this.birthdateError = null
      return true
    },
  },
  mounted() {
    const terms = this.branding?.terms_and_conditions
    if (terms?.enable_custom_terms_of_service) {
      this.termsOfServiceUrl = terms.custom_terms_of_service_url
    }
    if (terms?.enable_custom_privacy_policy) {
      this.privacyPolicy = terms.custom_privacy_policy_url
    }
    if (terms?.brand_name) {
      this.brandName = terms.brand_name
    }
    this.$nextTick(() => {
      const firstNameInput = document.getElementById('firstName')
      if (firstNameInput) {
        firstNameInput.focus()
      }
    })
  },
}
</script>

<style src="../../../assets/css/Modals/AuthenticationModals/SignUpModal.css" scoped></style>
